import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import { sendAgentMessage, loadConversations, loadConversationMessages, archiveConversation } from '../../services/erpAiAgentService';
import OCRConfigPanel from './components/OCRConfigPanel';

// ─── Quick questions ──────────────────────────────────────────────────────────
const QUICK_QUESTIONS = [
  { label: '¿Facturas pendientes?', icon: 'FileText', q: '¿Qué facturas tengo pendientes de pago?' },
  { label: 'Stock crítico', icon: 'Package', q: '¿Qué productos tienen stock crítico?' },
  { label: 'Margen bajo', icon: 'TrendingDown', q: '¿Qué productos tienen margen bajo?' },
  { label: 'Subidas de precio', icon: 'ArrowUpCircle', q: '¿Qué proveedores han subido precios recientemente?' },
  { label: 'Valor inmovilizado', icon: 'Wallet', q: '¿Cuánto dinero tengo inmovilizado en stock?' },
  { label: 'Situación financiera', icon: 'BarChart2', q: 'Analiza la situación financiera actual.' },
  { label: 'Resumen del día', icon: 'Sun', q: 'Hazme un resumen del día.' },
  { label: 'Alertas abiertas', icon: 'Bell', q: 'Resume las alertas abiertas del sistema.' },
];

// ─── Message bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
  const isUser = msg?.role === 'user';
  const sources = msg?.metadata?.sources || [];

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1
        ${isUser
          ? 'bg-[#D6EF4E]/15 border border-[#D6EF4E]/30'
          : 'bg-[#A78BFA]/15 border border-[#A78BFA]/30'}`}>
        {isUser
          ? <Icon name="User" size={14} className="text-[#D6EF4E]" />
          : <span className="text-sm">🤖</span>}
      </div>
      {/* Content */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed font-oranienbaum whitespace-pre-wrap
          ${isUser
            ? 'rounded-tr-sm bg-[#D6EF4E]/10 border border-[#D6EF4E]/20 text-white/90 ml-auto'
            : 'rounded-tl-sm bg-white/4 border border-white/8 text-white/85'}`}>
          {msg?.content}
        </div>

        {/* Sources */}
        {!isUser && sources?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {[...new Set(sources)]?.map((src, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum uppercase tracking-wider"
                style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.18)', color: 'rgba(167,139,250,0.7)' }}>
                {src}
              </span>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <span className="text-[10px] font-oranienbaum text-white/25 px-1">
          {new Date(msg.created_at || Date.now())?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const IAEmpresarial = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [error, setError] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'ocr_config'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load conversation list on mount
  useEffect(() => {
    loadConversations()?.then(setConversations)?.catch(() => {});
  }, []);

  const handleSend = useCallback(async (text) => {
    const msg = (text || input)?.trim();
    if (!msg || loading) return;

    setInput('');
    setError(null);

    // Optimistic user message
    const userMsg = { id: Date.now(), role: 'user', content: msg, created_at: new Date()?.toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    const controller = new AbortController();
    setAbortController(controller);

    try {
      const response = await sendAgentMessage(msg, conversationId, { current_page: '/ia-empresarial' });

      if (!conversationId && response?.conversation_id) {
        setConversationId(response?.conversation_id);
        // Refresh conversation list
        loadConversations()?.then(setConversations)?.catch(() => {});
      }

      const assistantMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response?.answer,
        metadata: { sources: response?.sources || [], model: response?.model },
        created_at: new Date()?.toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      // Handle 429 quota error specifically
      if (err?.code === 429 || err?.retryable === true) {
        // Show the assistant quota message in the chat if available
        if (err?.answer) {
          const quotaMsg = {
            id: Date.now() + 1,
            role: 'assistant',
            content: err?.answer,
            metadata: { sources: [], quota_error: true },
            created_at: new Date()?.toISOString(),
          };
          setMessages((prev) => [...prev, quotaMsg]);
        }
        // Also set the error banner with the user-friendly 429 message
        setError('Se ha alcanzado temporalmente el límite de uso de Gemini. Espera unos minutos y vuelve a intentarlo.');
        // Update conversation id if available
        if (!conversationId && err?.conversationId) {
          setConversationId(err?.conversationId);
        }
      } else {
        setError(err?.message || 'Error desconocido. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
      setAbortController(null);
      inputRef?.current?.focus();
    }
  }, [input, loading, conversationId]);

  const handleStop = () => {
    abortController?.abort();
    setLoading(false);
    setAbortController(null);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    setInput('');
    inputRef?.current?.focus();
  };

  const handleLoadConversation = async (conv) => {
    try {
      const msgs = await loadConversationMessages(conv?.id);
      setMessages(msgs);
      setConversationId(conv?.id);
      setShowSidebar(false);
      setError(null);
    } catch (err) {
      setError('Error cargando la conversación.');
    }
  };

  const handleArchive = async (convId, e) => {
    e?.stopPropagation();
    try {
      await archiveConversation(convId);
      setConversations((prev) => prev?.filter((c) => c?.id !== convId));
      if (conversationId === convId) handleNewConversation();
    } catch {}
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-screen overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* ── Conversations Sidebar ── */}
      {showSidebar && (
        <div className="w-72 flex-shrink-0 border-r flex flex-col"
          style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
          <div className="px-4 py-4 border-b flex items-center justify-between"
            style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <span className="font-oranienbaum text-white/70 text-sm font-semibold">Conversaciones</span>
            <button onClick={() => setShowSidebar(false)}
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/8 transition-colors">
              <Icon name="X" size={14} className="text-white/50" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
            {conversations?.length === 0 && (
              <p className="font-oranienbaum text-white/30 text-xs text-center py-8">Sin conversaciones anteriores</p>
            )}
            {conversations?.map((conv) => (
              <button key={conv?.id} onClick={() => handleLoadConversation(conv)}
                className={`w-full text-left px-3 py-2.5 rounded-xl group flex items-start gap-2 transition-all
                  ${conv?.id === conversationId ? 'bg-[#A78BFA]/12 border border-[#A78BFA]/20' : 'hover:bg-white/5 border border-transparent'}`}>
                <Icon name="MessageSquare" size={13} className="text-[#A78BFA]/60 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-oranienbaum text-white/70 text-xs truncate">{conv?.titulo || 'Conversación'}</p>
                  <p className="font-oranienbaum text-white/30 text-[10px] mt-0.5">
                    {new Date(conv.updated_at)?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </p>
                </div>
                <button onClick={(e) => handleArchive(conv?.id, e)}
                  className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded flex items-center justify-center hover:bg-red-500/20 transition-all">
                  <Icon name="Trash2" size={11} className="text-red-400/70" />
                </button>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* ── Main Chat Area ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Header */}
        <div className="flex-shrink-0 px-5 py-4 border-b flex items-center gap-3"
          style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(167,139,250,0.03)' }}>
          <button onClick={() => setShowSidebar(!showSidebar)}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/8 transition-colors">
            <Icon name="PanelLeft" size={16} className="text-white/50" />
          </button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)' }}>
            <span className="text-lg">🤖</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-oranienbaum font-bold text-white text-base tracking-wide">IA Empresarial</h1>
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${loading ? 'bg-[#A78BFA] animate-pulse' : 'bg-emerald-400'}`} />
              <span className="font-oranienbaum text-white/40 text-[11px]">
                {loading ? 'Analizando datos del ERP...' : 'Agente Gemini · Listo'}
              </span>
            </div>
          </div>
          {/* Tab buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setActiveTab('chat')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
              style={activeTab === 'chat'
                ? { background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.30)', color: '#A78BFA' }
                : { background: 'transparent', border: '1px solid transparent', color: 'rgba(255,255,255,0.40)' }}>
              <Icon name="MessageSquare" size={12} />
              Chat
            </button>
            <button
              onClick={() => setActiveTab('ocr_config')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
              style={activeTab === 'ocr_config'
                ? { background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.30)', color: '#60A5FA' }
                : { background: 'transparent', border: '1px solid transparent', color: 'rgba(255,255,255,0.40)' }}>
              <Icon name="ScanLine" size={12} />
              OCR
            </button>
          </div>
          <button onClick={handleNewConversation}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all hover:bg-[#A78BFA]/15"
            style={{ border: '1px solid rgba(167,139,250,0.20)', color: 'rgba(167,139,250,0.8)' }}>
            <Icon name="Plus" size={13} />
            Nueva
          </button>
        </div>

        {/* OCR Config Tab */}
        {activeTab === 'ocr_config' && (
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="max-w-2xl mx-auto">
              <OCRConfigPanel />
            </div>
          </div>
        )}

        {/* Messages area + Input (chat tab) */}
        {activeTab === 'chat' && (
          <>
        <div className="flex-1 overflow-y-auto px-4 py-6">
          {/* Empty state with quick questions */}
          {messages?.length === 0 && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.25)' }}>
                  <span className="text-3xl">🤖</span>
                </div>
                <h2 className="font-oranienbaum font-bold text-white text-xl mb-2">Agente IA Empresarial</h2>
                <p className="font-oranienbaum text-white/40 text-sm leading-relaxed">
                  Consulta información real del ERP en lenguaje natural.<br />
                  Analizo facturas, stock, márgenes, caja, ventas y alertas.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {QUICK_QUESTIONS?.map((q, i) => (
                  <button key={i} onClick={() => handleSend(q?.q)}
                    className="flex flex-col items-start gap-2 p-3 rounded-xl text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.25)'; e.currentTarget.style.background = 'rgba(167,139,250,0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.20)' }}>
                      <Icon name={q?.icon} size={14} className="text-[#A78BFA]" />
                    </div>
                    <span className="font-oranienbaum text-white/65 text-xs leading-snug">{q?.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="max-w-3xl mx-auto">
            {messages?.map((msg) => (
              <MessageBubble key={msg?.id} msg={msg} />
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex gap-3 mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                  style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.30)' }}>
                  <span className="text-sm">🤖</span>
                </div>
                <div className="px-4 py-3 rounded-2xl rounded-tl-sm"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="font-oranienbaum text-white/40 text-xs">Analizando datos del ERP...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 px-4 py-3 rounded-xl flex items-start gap-3"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.20)' }}>
                <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="font-oranienbaum text-red-300/80 text-sm">{error}</p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 px-4 pb-4 pt-3 border-t"
          style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.20)' }}>          <div className="max-w-3xl mx-auto">
            {/* Quick questions strip (when chat has messages) */}
            {messages?.length > 0 && (
              <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide">
                {QUICK_QUESTIONS?.slice(0, 4)?.map((q, i) => (
                  <button key={i} onClick={() => handleSend(q?.q)} disabled={loading}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-oranienbaum transition-all hover:bg-[#A78BFA]/15 disabled:opacity-40"
                    style={{ border: '1px solid rgba(167,139,250,0.18)', color: 'rgba(167,139,250,0.7)', background: 'rgba(167,139,250,0.05)' }}>
                    {q?.label}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-end gap-3 px-4 py-3 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e?.target?.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pregunta algo sobre tu empresa..."
                rows={1}
                disabled={loading}
                className="flex-1 bg-transparent resize-none outline-none font-oranienbaum text-white/85 text-sm placeholder-white/25 disabled:opacity-50"
                style={{ maxHeight: '120px', lineHeight: '1.5' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e?.target?.scrollHeight, 120) + 'px';
                }}
              />
              <div className="flex items-center gap-2 flex-shrink-0">
                {loading ? (
                  <button onClick={handleStop}
                    className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-red-500/20"
                    style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}
                    title="Detener">
                    <Icon name="Square" size={14} className="text-red-400" />
                  </button>
                ) : (
                  <button onClick={() => handleSend()} disabled={!input?.trim()}
                    className="w-9 h-9 rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
                    style={{
                      background: input?.trim() ? 'rgba(167,139,250,0.20)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${input?.trim() ? 'rgba(167,139,250,0.35)' : 'rgba(255,255,255,0.08)'}`,
                    }}
                    title="Enviar (Enter)">
                    <Icon name="Send" size={14} className={input?.trim() ? 'text-[#A78BFA]' : 'text-white/30'} />
                  </button>
                )}
              </div>
            </div>
            <p className="font-oranienbaum text-white/20 text-[10px] text-center mt-2">
              Solo consulta datos reales del ERP · Las acciones de escritura requieren confirmación
            </p>
          </div>
        </div>
          </>
        )} {/* end activeTab === 'chat' */}
      </div>    </div>
  );
};

export default IAEmpresarial;
